import { DeleteOutlined, PaperClipOutlined } from '@ant-design/icons';
import { Checkbox, Flex, Image } from 'antd';
import {
  type CSSProperties,
  type FC,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  AttachedImage,
  Box,
  Button,
  ImagePreviewButton,
  Space,
  Text,
  Tooltip,
  theme,
} from '..';
import { type StoredFiles, useDndFileUploader } from './hooks';

type Props = {
  files: File[];
  onChange: (acceptedFiles: File[]) => void;
  handleCancel: (index: number) => void;
  storedFiles?: {
    items: StoredFiles;
    onCancel: (index: number) => void;
  };
  disabled?: boolean;
  acceptableImage?: boolean;
  maxFiles?: number;
  maxFileSelection?: number;
  acceptablePdf?: boolean;
  withPreviewImage?: boolean;
  onSelectImage?: (value: boolean, name: string) => void;
  selectedImages?: string[];
  shouldWrapInFlex?: boolean;
  isLoading?: boolean;
  styles?: {
    [k in 'button' | 'inner']?: CSSProperties;
  };
} & (
  | { selectMode?: 'name-select'; selectable?: boolean }
  | { selectMode: 'area-select'; imageWidth?: number }
);

export const FileUploader: FC<Props> = ({
  files,
  onChange,
  handleCancel,
  storedFiles,
  disabled = false,
  acceptableImage = false,
  maxFiles = 5,
  acceptablePdf = false,
  withPreviewImage = true,
  onSelectImage,
  selectedImages,
  shouldWrapInFlex,
  isLoading,
  maxFileSelection = 20,
  styles,
  ...rest
}) => {
  const { t } = useTranslation();

  const { openDropzone, getInputProps, getRootProps } = useDndFileUploader({
    files,
    onChange,
    acceptableImage,
    disabled,
    maxFiles,
    acceptablePdf,
    storedFiles: storedFiles?.items,
    enableMaxFileCountControl: true,
  });

  const renderStoredFileItems = (
    fileList: StoredFiles,
    onCancel: (index: number) => void,
  ) => {
    return fileList.map((item, index) => (
      <UploadedFileItem
        // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
        key={`storedFile-${index}`}
        name={item.name}
        file={item.url}
        onDelete={() => {
          onCancel(index);
        }}
        canPreview={
          item.url !== null && item.fileContentType.startsWith('image')
        }
        onSelectImage={onSelectImage}
        selectedImages={selectedImages}
        maxFileSelection={maxFileSelection}
        selectable={rest.selectMode === 'area-select' ? false : rest.selectable}
        selectMode={rest.selectMode ?? 'name-select'}
        withPreviewImage={
          item.url !== null &&
          item.fileContentType.startsWith('image') &&
          withPreviewImage
        }
        areaSelectModeImageWidth={
          rest.selectMode === 'area-select' ? rest.imageWidth : undefined
        }
      />
    ));
  };

  const renderFileItems = (
    fileList: File[],
    onCancel: (index: number) => void,
  ) => {
    return fileList.map((file, index) => (
      <UploadedFileItem
        // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
        key={`file-${index}`}
        name={file.name}
        file={file}
        onDelete={() => {
          onCancel(index);
        }}
        canPreview={file.type.startsWith('image')}
        onSelectImage={onSelectImage}
        selectedImages={selectedImages}
        maxFileSelection={maxFileSelection}
        selectable={rest.selectMode === 'area-select' ? false : rest.selectable}
        selectMode={rest.selectMode ?? 'name-select'}
        withPreviewImage={file.type.startsWith('image') && withPreviewImage}
        areaSelectModeImageWidth={
          rest.selectMode === 'area-select' ? rest.imageWidth : undefined
        }
      />
    ));
  };

  const storedFileItems =
    storedFiles && storedFiles.items.length > 0
      ? renderStoredFileItems(storedFiles.items, storedFiles.onCancel)
      : null;

  const fileItems =
    files.length > 0 ? renderFileItems(files, handleCancel) : null;

  const isSteedMaxFile = useMemo(() => {
    const currentSetImageLength =
      (storedFiles?.items.length ?? 0) + (files.length ?? 0);
    if (!maxFiles) {
      return false;
    }
    return currentSetImageLength >= maxFiles;
  }, [files, storedFiles, maxFiles]);

  const fileContent = (
    <>
      {storedFileItems}
      {fileItems}
    </>
  );

  return (
    <div
      style={{
        flex: 1,
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        border: '1px',
        ...getRootProps().style,
      }}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <Space direction="vertical" style={styles?.inner}>
        {rest.selectMode === 'area-select' && (
          <Text
            color="secondary"
            variant="footnote"
            weight="regular"
            text={t('message-max-upload-file-information', {
              value: maxFileSelection,
            })}
          />
        )}
        <Button
          style={{ maxWidth: 224, ...styles?.button }}
          onClick={openDropzone}
          disabled={
            disabled || (rest.selectMode !== 'area-select' && isSteedMaxFile)
          }
          icon={<PaperClipOutlined />}
          loading={isLoading}
        >
          {t('upload')}
        </Button>
        <Image.PreviewGroup>
          {shouldWrapInFlex ? (
            <Flex wrap="wrap" gap={16}>
              {fileContent}
            </Flex>
          ) : (
            fileContent
          )}
        </Image.PreviewGroup>
      </Space>
    </div>
  );
};

const UploadedFileItem = ({
  name,
  file,
  onDelete,
  canPreview,
  selectable,
  onSelectImage,
  selectedImages,
  maxFileSelection,
  selectMode,
  withPreviewImage,
  areaSelectModeImageWidth = 316,
}: {
  name: string;
  file: string | File | null;
  onDelete: () => void;
  canPreview: boolean;
  selectable?: boolean;
  onSelectImage?: (value: boolean, name: string) => void;
  selectedImages?: string[];
  maxFileSelection: number;
  selectMode: 'name-select' | 'area-select';
  withPreviewImage: boolean;
  areaSelectModeImageWidth?: number;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const [previewUrl, setPreviewUrl] = useState('');
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (!file || !canPreview) {
      return;
    }

    if (typeof file === 'string') {
      setPreviewUrl(file);
    } else {
      const loadFile = async (file: File) => {
        const url = await new Promise<string>((resolve) => {
          const reader = new FileReader();

          reader.readAsDataURL(file);
          reader.onload = () => {
            resolve(reader.result as string);
          };
        });

        setPreviewUrl(url);
      };

      void loadFile(file);
    }
  }, [file, canPreview]);

  const checked = useMemo(
    () => !!selectedImages?.includes(name),
    [name, selectedImages],
  );

  const disabled = useMemo(
    () => !checked && selectedImages?.length === maxFileSelection,
    [checked, maxFileSelection, selectedImages],
  );

  const deleteImage = () => {
    setPreviewUrl('');
    onDelete();
  };
  const onCheck = () => {
    if (!disabled) {
      onSelectImage?.(!checked, name);
    }
  };

  return selectMode === 'name-select' ? (
    <Flex vertical align="flex-start">
      {withPreviewImage ? (
        <>
          {canPreview && previewUrl && selectable ? (
            <Flex style={{ width: 330 }}>
              <AttachedImage
                src={previewUrl}
                wrapperStyle={{ height: 200 }}
                style={{ objectPosition: 'unset' }}
                showPreview
              />
            </Flex>
          ) : (
            <Image src={previewUrl} alt={name} />
          )}
        </>
      ) : null}
      <Flex align="center">
        {selectable ? (
          <Checkbox checked={checked} onChange={onCheck} disabled={disabled}>
            <Text text={name} ellipsis maxWidth={270} />
          </Checkbox>
        ) : (
          <Text text={name} />
        )}
        <DeleteButton deleteImage={deleteImage} />
      </Flex>
    </Flex>
  ) : (
    <Flex
      vertical
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      onClick={onCheck}
      style={{ cursor: disabled ? 'default' : 'pointer' }}
    >
      <Box
        backgroundColor={token.colorBgContainerDisabled}
        width={areaSelectModeImageWidth}
        padding={8}
      >
        <Tooltip
          title={t('message-file-upload-limit-reached')}
          open={disabled && hover}
        >
          <Flex
            justify="center"
            style={{
              position: 'relative',
              border: `1px solid ${hover && !disabled ? token.colorPrimary : 'transparent'}`,
              boxShadow:
                hover && !disabled
                  ? '0 0 0 2px rgba(45, 175, 176, .5)'
                  : undefined,
              opacity: disabled ? 0.2 : 1,
            }}
          >
            <Checkbox
              style={{ position: 'absolute', top: 0, left: 3, zIndex: 10 }}
              checked={checked}
              disabled={disabled}
            />
            {checked && (
              <Box
                backgroundColor="rgba(36, 132, 100, 0.12)"
                zIndex={10}
                width="100%"
                height="100%"
                position="absolute"
                top={0}
                left={0}
              />
            )}
            <Image
              src={previewUrl}
              preview={false}
              alt={name}
              style={{ height: 200 }}
            />
          </Flex>
        </Tooltip>
      </Box>
      <Flex
        justify="space-between"
        align="center"
        style={{ width: areaSelectModeImageWidth }}
      >
        <Text text={name} ellipsis maxWidth={200} />
        <Flex align="center">
          <DeleteButton deleteImage={deleteImage} />
          <ImagePreviewButton
            previewURL={previewUrl}
            borderRadius={4}
            height={32}
            width={88}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

const DeleteButton = ({ deleteImage }: { deleteImage: () => void }) => (
  <Button
    type="text"
    icon={<DeleteOutlined />}
    onClick={(e) => {
      e.stopPropagation();
      deleteImage();
    }}
  />
);
